import { components, MenuListProps } from 'react-select';
import { DayClickEventHandler, DayPicker, SelectSingleEventHandler } from 'react-day-picker';
import useResizeObserver from '../useResizeObserver';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormType } from '../../../shared/models/form';
import { format } from 'date-fns';
import { getLocale } from '../utils';

const formatDate = (date?: Date): string => {
  if (!date) {
    return '';
  }
  return format(date, 'yyyy-MM-dd');
};

interface CustomMenuListProps {
  menuListProps: MenuListProps;
  isTripTypeRoundTrip: boolean;
  fieldIndex: number;
  onChange: (date: string) => void;
  onRangeChange: (newDepartureDate: string, newReturnDate: string) => void;
  onDateSelect?: (fieldIndex: number) => void;
  type: 'departure' | 'return';
}

const CustomMenuList = ({
  menuListProps,
  isTripTypeRoundTrip,
  fieldIndex,
  onChange,
  onRangeChange,
  onDateSelect,
  type,
}: CustomMenuListProps) => {
  const ref = useResizeObserver<HTMLDivElement>();

  const { control } = useFormContext<FormType>();

  const [watchedDeparture, watchedPreviousDeparture, watchedReturn] = useWatch({
    control,
    name: [
      `bounds.${fieldIndex}.departure`,
      `bounds.${fieldIndex - 1}.departure`,
      'bounds.0.return',
    ],
  });

  const departureDate = watchedDeparture ? new Date(`${watchedDeparture}T00:00:00`) : undefined;
  const previousDepartureDate = watchedPreviousDeparture
    ? new Date(`${watchedPreviousDeparture}T00:00:00`)
    : undefined;
  const returnDate = watchedReturn ? new Date(`${watchedReturn}T00:00:00`) : undefined;
  const today = new Date();

  const defaultMonthForRange =
    type === 'departure'
      ? departureDate ?? returnDate ?? today
      : returnDate ?? departureDate ?? today;

  const handleRangeSelect: DayClickEventHandler = (selectedDay) => {
    let newDeparture: Date | undefined;
    let newReturn: Date | undefined;

    const isReturnEarlierThanSelectedDay = returnDate && returnDate < selectedDay;
    const isDepartureLaterThanSelectedDay = departureDate && departureDate > selectedDay;

    if (type === 'departure') {
      newDeparture = selectedDay;
      newReturn = isReturnEarlierThanSelectedDay ? undefined : returnDate;
    }

    if (type === 'return') {
      newDeparture = isDepartureLaterThanSelectedDay ? selectedDay : departureDate;
      newReturn = isDepartureLaterThanSelectedDay ? undefined : selectedDay;
    }

    onRangeChange(formatDate(newDeparture), formatDate(newReturn));
  };

  const handleSingleSelect: SelectSingleEventHandler = (date) => {
    onChange(formatDate(date));
    if (onDateSelect) {
      onDateSelect(fieldIndex);
    }
    if (document.activeElement instanceof HTMLInputElement) {
      document.activeElement.blur();
    }
  };

  return (
    <components.MenuList {...menuListProps}>
      <div ref={ref}>
        {isTripTypeRoundTrip ? (
          <DayPicker
            locale={getLocale()}
            mode="range"
            selected={{ from: departureDate ?? returnDate, to: returnDate }}
            onDayClick={handleRangeSelect}
            disabled={{ before: today }}
            modifiersStyles={{
              selected: { backgroundColor: '#ffcb0b', color: 'black' },
            }}
            defaultMonth={defaultMonthForRange}
          />
        ) : (
          <DayPicker
            defaultMonth={previousDepartureDate ?? departureDate ?? today}
            disabled={{ before: previousDepartureDate ?? today }}
            locale={getLocale()}
            mode="single"
            modifiersStyles={{ selected: { backgroundColor: '#ffcb0b', color: 'black' } }}
            onSelect={handleSingleSelect}
            selected={departureDate}
          />
        )}
      </div>
    </components.MenuList>
  );
};

export default CustomMenuList;
